import { CarIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetCarriers } from '@/api/packages/get-carriers'
import { useRegenerateCarrierCode } from '@/api/packages/regenerate-carrier-code'
import { Modal } from '@/components/ui/modal'
import { Table } from '@/components/ui/table'
import { toastFactory } from '@/factories'
import { DriversAccessCodeModalProps } from '@/pages/packages/drivers-access-code-modal/drivers-access-code-modal.types'
import { getDriversAccessCodeModalColumns } from '@/pages/packages/drivers-access-code-modal/drivers-access-code-modal.utils'
import { getApiErrorMessage } from '@/utils'

import * as S from './drivers-access-code-modal.styled'

export const DriversAccessCodeModal = ({ buildingId, onClose }: DriversAccessCodeModalProps) => {
  const { t } = useTranslation(['packages'])
  const [regeneratingCarrierIds, setRegeneratingCarrierIds] = useState(new Set<number>())

  const { data: carriers = [], refetch } = useGetCarriers({ buildingId })
  const { mutateAsync: regenerateCode } = useRegenerateCarrierCode()

  const handleRegenerateCode = async (carrierId: number) => {
    setRegeneratingCarrierIds((prev) => new Set(prev).add(carrierId))

    try {
      await regenerateCode({ carrierId })
      await refetch()
      toastFactory.success(t('packages:driversAccessCodeModal.toastSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    } finally {
      setRegeneratingCarrierIds((prev) => {
        const newSet = new Set(prev)
        newSet.delete(carrierId)
        return newSet
      })
    }
  }

  const columns = useMemo(
    () =>
      getDriversAccessCodeModalColumns({
        handleRegenerateCode,
        regeneratingCarrierIds,
        t,
      }),
    [regeneratingCarrierIds, handleRegenerateCode, t],
  )

  return (
    <Modal
      title={t('packages:driversAccessCodeModal.title')}
      size="medium"
      headerIcon={CarIcon}
      onClose={onClose}
    >
      <S.ScrollableContent>
        <Table
          id="drivers-access-code"
          columns={columns}
          data={carriers}
          emptyText={t('packages:driversAccessCodeModal.emptyText')}
          getRowKey={(carrier) => String(carrier.id)}
        />
      </S.ScrollableContent>
    </Modal>
  )
}
